"use client";

import { useState } from "react";
import { Container } from "./Primitives";
import PartnerModal from "./PartnerModal";
import { splitLines, type CompanyPartner } from "@/lib/companySettings";

/**
 * Партнёрский CTA-блок. Текст из админки (`/company/partner`). Кнопка
 * открывает СВОЮ модалку «Стать партнёром» (город/имя/телефон/комментарий),
 * заявка уходит на partner-routing (email/telegram/hashtag).
 *
 * variant: "white" (главная) | "blue" (на /events, где фон белый).
 * Если текст не задан — блок не рендерится.
 */
export default function PartnerCTA({
  partner,
  variant = "white",
  cityNames,
  emailFrom,
}: {
  partner?: CompanyPartner;
  variant?: "white" | "blue";
  cityNames?: string[];
  emailFrom?: string;
}) {
  const [open, setOpen] = useState(false);
  const lines = splitLines(partner?.title);
  if (lines.length === 0) return null;

  const isBlue = variant === "blue";

  return (
    <section className={isBlue ? "" : "pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-[85px] lg:pb-[85px]"}>
      <Container>
        <div
          className={`flex flex-col items-start gap-7 rounded-[24px] p-8 sm:gap-10 sm:rounded-[30px] sm:p-12 lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:p-[83px] ${
            isBlue ? "bg-primary" : "bg-white"
          }`}
        >
          <h2
            className={`max-w-[560px] text-[24px] font-medium leading-[1.1] tracking-[-0.5px] sm:text-[28px] lg:text-[34px] xl:text-[38px] xl:tracking-[-1px] ${
              isBlue ? "text-white" : "text-black"
            }`}
          >
            {lines.map((line, i) => (
              <span key={i} className="block">
                {line}
              </span>
            ))}
          </h2>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className={`inline-flex shrink-0 items-center gap-2 rounded-[70px] px-6 py-3.5 text-[15px] font-medium transition sm:px-8 sm:py-4 sm:text-base ${
              isBlue
                ? "bg-white text-text hover:bg-white/90"
                : "bg-primary text-white hover:bg-primary-hover"
            }`}
          >
            <span>Стать партнёром</span>
            {/* SVG-стрелка наследует currentColor → всегда в цвет текста кнопки
                (тёмная на белой кнопке синего блока, белая на синей кнопке). */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </Container>

      <PartnerModal
        open={open}
        onClose={() => setOpen(false)}
        cityNames={cityNames}
        routing={{
          email: partner?.email,
          telegramChatId: partner?.telegram_chat_id,
          hashtag: partner?.hashtag,
          emailFrom,
        }}
      />
    </section>
  );
}
